// src/lib/notes.js
// STATUS: active | UPDATED: 2026-09-17
// LOG:
//   2026-09-17  New. Reports Jazz can read and answer without composing an
//               email about it.
//
// A note is a report, plan, question or update, living inside the product
// rather than in his inbox. See supabase/migrations/0028 for why, and for the
// Cimarron Pulse original this is ported from.
//
// The one rule worth stating in code: opening a note is a READ RECEIPT, not an
// edit. It is set once, by the first person who is not the author, and never
// unset. The whole value of it is being able to tell "he has not looked at it"
// apart from "he looked and had nothing to say" before we go and chase him.

import { supabase } from './supabase'

const CARD = `id, slug, title, kicker, summary, kind, author, sheets, stage,
  read_at, approved_at, stage_changed_at, position, entry_count, last_entry_at,
  phases, decisions, published_at`

export const STAGES = ['sent', 'read', 'discussing', 'approved', 'parked', 'building', 'done']

// What each stage means in a sentence, for the UI rather than for the database.
export const STAGE_COPY = {
  sent: { label: 'Waiting on Jazz', hint: 'Posted. He has not opened it yet.' },
  read: { label: 'Read', hint: 'He opened it and has not said anything yet.' },
  discussing: { label: 'Talking about it', hint: 'There is a thread going.' },
  approved: { label: 'Approved', hint: 'He said build it.' },
  parked: { label: 'Parked', hint: 'Not now. Recoverable, unlike dropped.' },
  building: { label: 'Building', hint: 'In progress.' },
  done: { label: 'Done', hint: 'Shipped.' },
}

// Who the employee is. One row, so the name is an update rather than a deploy.
// Cached for the page's lifetime because it changes roughly never.
let agentCache = null
export async function getAgent() {
  if (agentCache) return agentCache
  const { data } = await supabase.from('agent').select('name, title, tagline, state').eq('id', 'coach').maybeSingle()
  agentCache = data || { name: 'The coach', title: 'The Academy coach' }
  return agentCache
}

export async function listNotes() {
  const { data, error } = await supabase
    .from('notes')
    .select(CARD)
    .order('published_at', { ascending: false })
  return { data: data || [], error }
}

export async function getNote(slug) {
  const { data, error } = await supabase
    .from('notes')
    .select(`${CARD}, body_html`)
    .eq('slug', slug)
    .maybeSingle()
  return { data, error }
}

export async function listEntries(noteId) {
  if (!noteId) return { data: [], error: null }
  const { data, error } = await supabase
    .from('note_entries')
    .select('id, author, author_name, kind, body, attachments, created_at')
    .eq('note_id', noteId)
    .order('created_at')
  return { data: data || [], error }
}

// Set once and never unset. Called on open by somebody who did not write it.
export async function markRead(note, userId) {
  if (!note?.id || note.read_at || !userId) return { data: null, error: null }
  const patch = { read_at: new Date().toISOString(), read_by: userId, updated_at: new Date().toISOString() }
  // Only lift a card out of 'sent'. A note that is already being discussed or
  // approved must not be dragged backwards by somebody re-opening it.
  if (note.stage === 'sent') {
    patch.stage = 'read'
    patch.stage_changed_at = patch.read_at
  }
  const { data, error } = await supabase.from('notes').update(patch).eq('id', note.id).select(CARD).single()
  return { data, error }
}

export const NOTE_BUCKET = 'note-files'

// Private bucket, so everything is served through a short lived signed URL
// rather than a public path. See migration 0030.
export async function signedUrl(path, seconds = 3600) {
  if (!path) return null
  const { data } = await supabase.storage.from(NOTE_BUCKET).createSignedUrl(path, seconds)
  return data?.signedUrl || null
}

// Names are kept for display and never used as the storage key. A key built
// from a filename breaks on the first space, accent or slash, and two people
// attaching "scan.pdf" would overwrite each other.
export async function uploadFile(noteId, file) {
  if (!noteId || !file) return { data: null, error: 'Nothing to upload.' }
  if (file.size > 25 * 1024 * 1024) return { data: null, error: 'That file is over 25MB.' }

  const ext = (file.name.match(/\.([A-Za-z0-9]{1,8})$/) || [])[1] || 'bin'
  const key = `${noteId}/${crypto.randomUUID()}.${ext.toLowerCase()}`
  const { error } = await supabase.storage.from(NOTE_BUCKET).upload(key, file, {
    contentType: file.type || 'application/octet-stream',
    upsert: false,
  })
  if (error) return { data: null, error: error.message }
  return { data: { path: key, name: file.name, type: file.type || '', size: file.size }, error: null }
}

export async function reply({ noteId, userId, authorName, body, attachments = [] }) {
  const text = String(body || '').trim()
  // A reply that is only a file is a real reply. Requiring words to attach
  // something is the kind of rule that makes people email it instead.
  if (!noteId || (text.length === 0 && attachments.length === 0)) {
    return { data: null, error: 'Nothing to say.' }
  }
  const { data, error } = await supabase
    .from('note_entries')
    .insert({
      note_id: noteId,
      author: 'jazz',
      author_name: authorName || '',
      kind: 'reply',
      body: text.slice(0, 8000),
      attachments,
      created_by: userId || null,
    })
    .select('id, author, author_name, kind, body, attachments, created_at')
    .single()
  return { data, error }
}

// Moving a card writes a system row in the same breath, so the thread reads as
// a history instead of a card whose state changed for reasons nobody recorded.
export async function moveTo({ note, stage, userId, authorName }) {
  if (!note?.id || !STAGES.includes(stage)) return { data: null, error: 'Unknown stage.' }
  const now = new Date().toISOString()
  const patch = { stage, stage_changed_at: now, updated_at: now }
  if (stage === 'approved') {
    patch.approved_at = now
    patch.approved_by = userId || null
  }

  const { data, error } = await supabase.from('notes').update(patch).eq('id', note.id).select(CARD).single()
  if (error) return { data: null, error }

  await supabase.from('note_entries').insert({
    note_id: note.id,
    author: 'system',
    author_name: authorName || '',
    kind: 'system',
    body: `Moved to ${STAGE_COPY[stage]?.label || stage}.`,
    created_by: userId || null,
  })

  return { data, error: null }
}
